import { get } from "lodash";
import { InputDropdownMulti } from "../inputs";
import { gql, useMutation, useQuery } from "@apollo/client";
import { FormProvider, useForm } from "react-hook-form";
import { useAuth } from "../../../context/AuthContext";
import { motion } from "framer-motion";
import { BUCKET_ORGS } from "../../../constants";

const UPDATE_PREFERENCES = gql`
  mutation updatePreferences($meta: UpdatePreferences!) {
    updatePreferences(meta: $meta) {
      id
    }
  }
`;

export const StepPreferences = ({ step, onSuccess }: any) => {
  const { session } = useAuth();
  const methods = useForm({
    values: {
      metadata: {
        preferred_countries:
          get(session, "user.metadata.preferred_countries", [])?.map(
            (i: any) => ({
              id: i.id,
              label: i.name,
            }),
          ) || [],
        preferred_agbase_categories:
          get(session, "user.metadata.preferred_agbase_categories", [])?.map(
            (i: any) => ({ id: i.id, label: i.name }),
          ) || [],
        preferred_agbase_value_chains:
          get(session, "user.metadata.preferred_agbase_value_chains", [])?.map(
            (i: any) => ({ id: i.id, label: i.name }),
          ) || [],
      },
    },
  });

  const [updatePreferences, { loading: updateLoading }] = useMutation(
    UPDATE_PREFERENCES,
    {
      onCompleted: () => {
        onSuccess();
      },
      onError: (error) => {
        console.error("Error updating preferences:", error);
      },
    },
  );

  const { data, loading, error } = useQuery(
    gql`
      query queryMetadataPreferences(
        $search: SearchRequest!
        $agg: AggregationRequest!
      ) {
        countries: search(search: $search) {
          items
          total
          took
          cursor
        }

        metadata: aggregate(aggregations: $agg) {
          aggregations
          took
        }
      }
    `,
    {
      variables: {
        search: {
          bucket: "countries",
          size: 500,
          sort: [
            {
              key: "name.keyword",
              dir: "asc",
            },
          ],
          query: {},
        },
        agg: {
          bucket: BUCKET_ORGS,
          query: {},
          list: [
            {
              key: "categories",
              type: "terms",
              params: {
                field: "agbase_category.name.keyword",
                size: 150,
              },
            },
            {
              key: "agg",
              type: "terms",
              params: {
                field: "agbase_value_chain.name.keyword",
                size: 150,
              },
            },
          ],
        },
      },
    },
  );

  const onSubmit = (data: any) => {
    const meta = {
      countries_ids:
        data.metadata.preferred_countries?.map((c: any) => c.id) || [],
      categories:
        data.metadata.preferred_agbase_categories?.map((c: any) => c.id) || [],
      value_chain:
        data.metadata.preferred_agbase_value_chains?.map((c: any) => c.id) ||
        [],
    };

    updatePreferences({
      variables: { meta },
    });
  };
  return (
    <div className="flex w-full flex-col gap-4 py-8">
      <div className="w-full flex items-center gap-5">
        <p className="text-2xl font-semibold grow">Select your preferences</p>
        <motion.button
          onClick={() => onSuccess()}
          className="bg-gray-100 px-5 py-1 rounded-md text-ab-darkgreen font-medium"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          transition={{ type: "spring", stiffness: 400, damping: 17 }}
        >
          Continue without preferences
        </motion.button>
      </div>
      <p>
        Adding your preferences for countries, categories, and value chains on
        the platform will ensure you receive personalized updates and insights,
        keeping you informed on relevant developments in your areas of interest.
      </p>
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className="flex flex-col justify-start items-start gap-4 text-sm"
        >
          <InputDropdownMulti
            options={get(data, "countries.items", []).map((c: any) => ({
              id: c.id,
              label: c.name,
            }))}
            label="Countries of interest"
            name="metadata.preferred_countries"
          />
          <InputDropdownMulti
            options={get(
              data,
              "metadata.aggregations.categories.buckets",
              [],
            ).map((bucket: any) => ({
              id: bucket.key,
              label: bucket.key,
            }))}
            label="Categories of interest"
            name="metadata.preferred_agbase_categories"
          />
          <InputDropdownMulti
            options={get(data, "metadata.aggregations.agg.buckets", []).map(
              (bucket: any) => ({
                id: bucket.key,
                label: bucket.key,
              }),
            )}
            label="Value Chains of interest"
            name="metadata.preferred_agbase_value_chains"
          />
          <button
            type="submit"
            disabled={updateLoading}
            className="bg-uw-primary px-4 py-1 rounded-md text-uw-text-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {updateLoading ? "Saving..." : "Save your preferences"}
          </button>
        </form>
      </FormProvider>
    </div>
  );
};
